import { useEffect, useMemo, useRef, useState } from 'react';

import type { QueryKey } from '@tanstack/react-query';

import type {
  IDataTable,
  IFilterMap,
  IFiltersList,
  IFiltersLocalization,
  ISelectFilter,
  TFilters,
  TFiltersParams,
  TTextValuePair,
} from '../../types/data_table';

import {
  sortOptionsLikeFilter,
  withFieldParam,
  withMultiSelectParam,
  withSelectParam,
} from './filterParams';
import {
  buildFiltersList,
  groupFilterIdsByType,
  preserveSelections,
  seedParamsFromFilters,
} from './filtersList';
import {
  mergeFiltersLocalization
} from './getDefaultFiltersLocalization';

export interface IUseDataTableFilters {
  lang: IDataTable['lang'];
  /** The consumer's `filters` config, or `undefined` for an unfiltered table. */
  filters: IDataTable['filters'];
  /** Changing this resets every filter — see `queries.resetFiltersParams`. */
  resetSignal?: QueryKey;
}

export interface IDataTableFilters {
  /** Filters as the `FiltersBar` renders them, including current selections. */
  filtersList: IFiltersList;
  /** Applied filters, flattened into request params. */
  params: TFiltersParams;
  /** Filter ids grouped by type, reported through `onResponseChange`. */
  filtersTypes?: Record<TFilters, string[]>;
  /**
   * `false` until default selections have been seeded into `params`. The table
   * must not fetch before this flips, or it fires once without the defaults
   * and again with them.
   */
  paramsReady: boolean;
  /** Bumped on reset to force the bar to remount with clean internal state. */
  filtersBarRenderKey: number;
  filtersChangeHandler: (
    id: string,
    type: TFilters,
    options?: string | TTextValuePair | TTextValuePair[],
    queryParamType?: IFilterMap['multiSelectionParamType']
  ) => void;
  resetFilters: () => void;
  filtersLocalization: IFiltersLocalization;
}

/**
 * Owns everything about the filters bar's *state*: the list it renders, the
 * params it produces, and keeping both in step with the `filtersMap` and
 * language coming from the consumer.
 *
 * The container reads `params` and `paramsReady` to drive the query; it never
 * touches the shapes in between.
 */
export const useDataTableFilters = ({
  lang,
  filters,
  resetSignal,
}: IUseDataTableFilters): IDataTableFilters => {
  const {
    filtersMap,
    localization,
    isLoading: isFiltersLoading = false,
    isError: isFiltersError = false,
  } = filters || {};

  const filtersLocalization = mergeFiltersLocalization(lang, localization);

  /**
   * A failed filters request is terminal: no map is coming, so the table must
   * stop waiting for one. Without this, a consumer whose `isLoading` stays
   * true on failure would leave the table stuck on its skeleton forever.
   */
  const areFiltersSettled = !isFiltersLoading || isFiltersError;

  const [filtersList, setFiltersList] = useState<IFiltersList>([]);
  const [params, setParams] = useState<TFiltersParams>({});
  const [filtersTypes, setFiltersTypes] =
    useState<Record<TFilters, string[]>>();
  const [filtersBarRenderKey, setFiltersBarRenderKey] = useState(0);
  const [paramsReady, setParamsReady] = useState(false);

  const defaultsInitializedRef = useRef(false);

  /** Guards the reset effect, which must not fire on the initial mount. */
  const hasMountedRef = useRef(false);

  /**
   * Content fingerprint of everything the default list is built from.
   *
   * Compared by value, not by identity, because consumers write `filters` and
   * its `localization` as inline literals — a new object on every parent
   * render. Keying off identity rebuilt the list constantly, and each rebuild
   * reached the filters bar as brand new state.
   */
  const filtersSignature = JSON.stringify([
    lang,
    filtersMap,
    filtersLocalization,
  ]);

  /** The list as it looks with nothing selected. */
  const filtersDefaultValues = useMemo(
    () => buildFiltersList(filtersMap, filtersLocalization?.all),
    [filtersSignature]
  );

  /** Updates one filter's rendered state and its entry in `params`. */
  const filtersChangeHandler: IDataTableFilters['filtersChangeHandler'] = (
    id,
    type,
    options,
    queryParamType
  ) => {
    if (type !== 'select') {
      const value = options as string | undefined;

      setFiltersList((prevFilters) =>
        prevFilters.map((filter) =>
          filter.id === id
            ? // A cleared search filter keeps rendering, so it needs an empty
              // string rather than `undefined`, which would uncontrol the input.
              { ...filter, value: type === 'search' ? value || '' : value }
            : filter
        )
      );
      setParams((prevParams) => withFieldParam(prevParams, id, value));

      return;
    }

    const filter = filtersList.find((item) => item.id === id) as
      | ISelectFilter
      | undefined;

    const selectedOptions = Array.isArray(options)
      ? sortOptionsLikeFilter(options, filter?.options || [])
      : (options as TTextValuePair | undefined);

    setFiltersList((prevFilters) =>
      prevFilters.map((item) =>
        item.id === id ? { ...item, selectedOptions } : item
      )
    );

    setParams((prevParams) =>
      Array.isArray(selectedOptions)
        ? withMultiSelectParam(
            prevParams,
            id,
            selectedOptions,
            filter?.options?.length ?? -1,
            queryParamType
          )
        : withSelectParam(prevParams, id, selectedOptions)
    );
  };

  /** Clears every selection and remounts the bar. */
  const resetFilters = () => {
    setFiltersList(filtersDefaultValues);
    setParams({});
    setFiltersBarRenderKey((prevKey) => prevKey + 1);
  };

  // Seed the default selections, once, as soon as a usable `filtersMap` exists.
  useEffect(() => {
    if (defaultsInitializedRef.current) return;

    if (filtersMap === undefined || (!filtersMap.length && areFiltersSettled)) {
      // No filters to seed into `params` — enable the query immediately.
      // An empty map leaves `defaultsInitializedRef` untouched so a map that
      // arrives later (e.g. filters fetched asynchronously) still initializes,
      // and it keeps waiting while `isLoading` says those filters are on their
      // way, so the table isn't fetched once without them and again with them.
      defaultsInitializedRef.current = filtersMap === undefined;
      setParamsReady(true);
      return;
    }

    if (filtersMap.length) {
      setFiltersList(filtersDefaultValues);
      setFiltersTypes(groupFilterIdsByType(filtersDefaultValues));

      // Seeded values go into `params` here, in the same commit that opens the
      // query. This used to be left to an effect inside `FiltersBar` and
      // sequenced with `setTimeout(..., 0)` — racing the event loop to make
      // one effect run before another, which React gives no guarantee about.
      setParams(seedParamsFromFilters(filtersDefaultValues));

      defaultsInitializedRef.current = true;
      setParamsReady(true);
    }
  }, [filtersMap, isFiltersLoading, isFiltersError]);

  // Re-label the filters when the language (or the map itself) changes, while
  // holding on to whatever the user has already selected.
  useEffect(() => {
    setFiltersList((prevFilters) =>
      preserveSelections(filtersDefaultValues, prevFilters || [])
    );
  }, [filtersSignature]);

  // Consumers signal "start over" by changing `resetFiltersParams`.
  //
  // Skipped on mount: there is nothing to reset yet, and running it would
  // clear the params the init effect has just seeded from the filters.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }

    resetFilters();
  }, [resetSignal]);

  return {
    filtersList,
    params,
    filtersTypes,
    paramsReady,
    filtersBarRenderKey,
    filtersChangeHandler,
    resetFilters,
    filtersLocalization
  };
};
